<script setup lang="ts">
import { computed, nextTick, onMounted, watch } from "vue";
import { Handle, Position, useVueFlow, type Edge } from "@vue-flow/core";
import type {
  ConditionBranchNode,
  ConditionBranchOutput,
} from "./types/ConditionBranchNode";
import { createConditionBranchOutput } from "./utils/dialogueProject";

const props = withDefaults(
  defineProps<{
    id: string;
    node: ConditionBranchNode;
    edges?: Edge[];
    selected?: boolean;
    exportIndex?: number;
    annotation?: string;
  }>(),
  {
    edges: () => [],
    selected: false,
    annotation: "",
  },
);

const emit = defineEmits<{ "update:annotation": [value: string] }>();

const { updateNodeInternals } = useVueFlow();
const outputSignature = computed(() =>
  props.node.outputs.map((output) => output.id).join("|"),
);

function nextOutputIndex() {
  return props.node.outputs.reduce((maximum, output) => {
    const match = /^分支\s+(\d+)$/.exec(output.label.trim());
    return match ? Math.max(maximum, Number(match[1])) : maximum;
  }, props.node.outputs.length);
}

function addOutput() {
  props.node.outputs.push(createConditionBranchOutput(nextOutputIndex()));
}

function deleteOutput(output: ConditionBranchOutput) {
  const index = props.node.outputs.findIndex((item) => item.id === output.id);
  if (index >= 0) props.node.outputs.splice(index, 1);
}

function ensureOutputLabel(output: ConditionBranchOutput, index: number) {
  if (!output.label.trim()) output.label = `分支 ${index + 1}`;
}

function isConnected(output: ConditionBranchOutput) {
  return props.edges.some(
    (edge) =>
      edge.source === props.id && edge.sourceHandle === output.id,
  );
}

function preventGraphDeletion(event: KeyboardEvent) {
  if (event.key === "Backspace" || event.key === "Delete") event.stopPropagation();
}

async function refreshHandles() {
  await nextTick();
  updateNodeInternals([props.id]);
}

watch(outputSignature, refreshHandles, { flush: "post" });
onMounted(refreshHandles);
</script>

<template>
  <article class="condition-branch-node" :class="{ selected }">
    <header class="condition-header">
      <span v-if="exportIndex !== undefined" class="export-index" :aria-label="`TimeLineGroup 索引 ${exportIndex}`" title="导出后的 TimeLineGroup 全局索引，从 0 开始，可用于播放调试">#{{ exportIndex }}</span>
      <input class="nodrag condition-kind graph-node-annotation" :value="annotation" aria-label="节点备注（仅编辑器显示，不参与导出）" :title="annotation || '点击填写节点备注，仅保存在工程中'" :placeholder="node.name || '条件判断'"
        @input="emit('update:annotation', ($event.target as HTMLInputElement).value)" @pointerdown.stop @mousedown.stop @click.stop @keydown.stop />
      <div class="condition-header-meta">
        <span class="output-count">{{ node.outputs.length }} 个出口</span>
        <button
          type="button"
          class="nodrag add-output-button"
          title="新增出口"
          @click.stop="addOutput"
        >
          ＋ 出口
        </button>
      </div>
    </header>

    <div class="condition-ports">
      <div
        v-for="(output, index) in node.outputs"
        :key="output.id"
        class="condition-port-row"
      >
        <span class="condition-input-label">
          <Handle
            v-if="index === 0"
            id="input"
            class="condition-input-handle"
            type="target"
            :position="Position.Left"
            title="条件分支入口"
            aria-label="条件分支入口"
          />
          <template v-if="index === 0">入口</template>
        </span>

        <span class="connection-indicator">
          <span v-if="isConnected(output)" class="connected-dot" title="已连接" />
        </span>
        <div class="condition-output-fields">
          <input
            v-model="output.label"
            class="nodrag condition-output-name"
            :aria-label="`出口 ${index + 1} 名称`"
            @blur="ensureOutputLabel(output, index)"
            @click.stop
          />
          <label class="condition-field">
            <span>表达式 <small>字符串</small></span>
            <textarea
              v-model="output.condition"
              class="nodrag nopan nowheel"
              :aria-label="`出口 ${index + 1} 表达式`"
              placeholder="输入此出口的表达式"
              rows="2"
              spellcheck="false"
              @pointerdown.stop
              @click.stop
              @keydown="preventGraphDeletion"
              @keyup="preventGraphDeletion"
            />
          </label>
        </div>
        <button
          type="button"
          class="nodrag delete-output-button"
          title="删除出口并断开对应连线"
          :aria-label="`删除出口 ${index + 1}`"
          @click.stop="deleteOutput(output)"
        >
          ×
        </button>
        <Handle
          :id="output.id"
          class="condition-output-handle"
          type="source"
          :position="Position.Right"
          :title="output.label"
          :aria-label="output.label"
        />
      </div>

      <div v-if="!node.outputs.length" class="condition-port-row no-outputs">
        <span class="condition-input-label">
          <Handle
            id="input"
            class="condition-input-handle"
            type="target"
            :position="Position.Left"
            title="条件分支入口"
            aria-label="条件分支入口"
          />
          入口
        </span>
        <span>暂无出口，请点击“＋ 出口”</span>
      </div>
    </div>
  </article>
</template>

<style scoped>
.export-index { flex-shrink: 0; padding: 2px 6px; border: 1px solid #d0c0e8; border-radius: 5px; color: #75519a; background: #fff; font-size: 12px; font-weight: 700; font-variant-numeric: tabular-nums; }
.condition-header { gap: 7px; }
.condition-kind { margin-right: auto; }
.condition-branch-node {
  --dsfg-handle-fill: #9d79c5;
  --dsfg-handle-ring: #fff;
  width: 300px;
  overflow: visible;
  color: #475569;
  background: #fff;
  border: 1px solid #d8cbe9;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(62, 45, 87, 0.10);
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease,
    transform 0.15s ease;
}

.condition-branch-node:hover {
  border-color: #b5a0d0;
}

.condition-branch-node.selected {
  border-color: #9770c5;
  box-shadow:
    0 0 0 2px rgba(151, 112, 197, 0.20),
    0 10px 28px rgba(62, 45, 87, 0.14);

}

.condition-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 34px;
  padding: 0 8px 0 11px;
  background: #ece3fa;
  border-radius: 9px 9px 0 0;
}

.condition-kind {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
}

.graph-node-annotation {
  box-sizing: border-box;
  flex: 1;
  min-width: 0;
  width: 100%;
  padding: 3px 5px;
  color: #705b86;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 4px;
  outline: 0;
  font: inherit;
  font-size: 11px;
  font-weight: 700;
  text-overflow: ellipsis;
}
.graph-node-annotation::placeholder { color: inherit; opacity: 1; }
.graph-node-annotation:hover, .graph-node-annotation:focus { background: #fff; border-color: #cbb9df; }
.graph-node-annotation:focus-visible { outline: 2px solid #9770c5; outline-offset: 1px; }

.condition-header-meta { display: flex; flex-shrink: 0; align-items: center; gap: 8px; }
.output-count { color: #7b6d8f; font-size: 9px; white-space: nowrap; }

.add-output-button {
  padding: 4px 7px;
  color: #79569e;
  background: #ffffffb3;
  border: 1px solid #d8cbe9;
  border-radius: 5px;
  font-size: 10px;
  cursor: pointer;
}

.add-output-button:hover {
  background: #fff;
}

.condition-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 4px 0 0;
  color: #7b6d8f;
  font-size: 10px;
}

.condition-field > span {
  display: flex;
  justify-content: space-between;
  color: #736085;
  font-weight: 600;
}

.condition-field small {
  color: #8b809a;
  font-weight: 400;
}

.condition-field textarea {
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  padding: 6px;
  color: #475569;
  background: #fff;
  border: 1px solid #d8cbe9;
  border-radius: 4px;
  resize: none;
  font: inherit;
  font-size: 11px;
  line-height: 1.5;
}

.condition-field textarea:focus {
  outline: 1px solid #9770c5;
}

.condition-field textarea::placeholder {
  color: #8b809a;
}

.condition-ports {
  overflow: visible;
  border-top: 1px solid #e1d8ed;
  border-radius: 0 0 9px 9px;
}

.condition-port-row {
  position: relative;
  display: grid;
  grid-template-columns: 32px 9px minmax(0, 1fr) 24px;
  align-items: center;
  min-height: 32px;
  padding: 8px 7px 8px 11px;
  background: #faf8fd;
  border-bottom: 1px solid #e8e1f0;
}

.condition-port-row:last-child {
  border-bottom: 0;
  border-radius: 0 0 9px 9px;
}

.condition-input-label {
  color: #7b6d8f;
  font-size: 10px;
}

.condition-output-fields {
  min-width: 0;
}

.condition-output-name {
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  padding: 4px 6px;
  color: #635076;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 4px;
  outline: 0;
  text-align: left;
  font: inherit;
  font-size: 10px;
}

.condition-output-name:hover,
.condition-output-name:focus {
  background: #fff;
  border-color: #b5a0d0;
}

.connected-dot {
  display: block;
  width: 5px;
  height: 5px;
  background: #3b9973;
  border-radius: 50%;
  box-shadow: 0 0 5px rgba(59, 153, 115, 0.15);
}

.delete-output-button {
  width: 22px;
  height: 22px;
  padding: 0;
  color: #8b809a;
  background: transparent;
  border: 0;
  border-radius: 4px;
  font-size: 16px;
  cursor: pointer;
}

.delete-output-button:hover {
  color: #b45367;
  background: #fcecf1;
}

.condition-input-handle,
.condition-output-handle {
  z-index: 4;
}

.no-outputs {
  grid-template-columns: 48px 1fr;
  color: #94651c;
  background: #fff8e8;
  font-size: 9px;
}

.no-outputs > span:last-child {
  text-align: right;
}

.condition-header { color: #79569e; border-bottom: 1px solid #d8cbe9; }
.condition-branch-node button { font-family: inherit; }
.condition-branch-node button:focus-visible, .condition-output-name:focus-visible { outline: 2px solid #9770c5; outline-offset: 2px; }
@media (prefers-reduced-motion: reduce) { .condition-branch-node { transition: none; } }

</style>
